'use client';
import {
  Badge,
  Box,
  Checkbox,
  TextField,
  Button,
  Autocomplete,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Typography,
  FormControlLabel,
  IconButton,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { FilterList, Inventory, Search, ArrowDropDown } from '@mui/icons-material';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import { useRouter } from 'next/navigation';
import { MaterialFilterDialog } from './MaterialFilterDialog';
import {
  useSampleFilterStore,
  defaultFilterCriteria,
  hasExtendedFiltersSet,
  type FilterSourceFlags,
} from '@/stores/sampleFilterStore';
import { substrateDefaultFilterCriteria, hasSubstrateExtendedFiltersSet } from '@/stores/substrateFilterStore';
import { useSampleListFilterStore } from '@/hooks/useSampleListFilterStore';
import type { SampleListMode } from '@/lib/sampleList/sampleListMode';
import { invalidateSampleGridCache } from '@/lib/cache/sampleGridCache';
import { log } from '@/lib/utils/logger';
import { filterAutocompleteOptionsByMatchRank } from '@/lib/utils/autocompleteMatchRank';
import { useDialogStore } from '@/stores/dialogStore';
import {
  OperationEnum,
  PrivilegeEnum,
  SampleSearchKind,
  StatusDto,
  type ReportingCategoryDto,
  type CharacterizationTechniqueDto,
  type SampleSearchCriteria,
  type SynthesisTechniqueDto,
} from '@/api/ApiClient';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { KeyValueType, OptionKey } from '@/lib/options/optionTypes';
import { STATUS_MAP } from '@/lib/types/statusTypes';
import { EntityAutocompleteWithLookup } from '@/components/common/EntityAutocompleteWithLookup';
import { useAuthStore } from '@/stores/authStore';
import { useBackend } from '@/hooks/useBackend';
import { usePrivileges } from '@/hooks/usePrivileges';
import { canCreateWithoutProject } from '@/lib/auth/unassignedSampleAccess';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { uniqueSortedDropdownStrings } from '@/lib/utils/substrateLookup';
import { useReopenSearchPanelOnFailure } from '@/hooks/useReopenSearchPanelOnFailure';
import { markNextSearchUserInitiated } from '@/stores/searchUiStore';
import {
  SAMPLE_SEARCH_KIND_OPTIONS,
  SAMPLE_SEARCH_NO_PROJECT,
  applySampleSearchKind,
  buildDefaultSampleSearchCriteriaForUser,
  defaultSampleSearchKindForUser,
} from '@/lib/utils/sampleSearchKind';

const OPTION_KEYS: OptionKey[] = [
  'synthesisInstruments',
  'synthesisTechniques',
  'synthesisTechniquesLeafOnly',
  'synthesisInstrumentOptions',
  'characterizationInstruments',
  'characterizationTechniques',
  'characterizationInstrumentOptions',
  'sampleMaterials',
  'sampleMaterialOptions',
  'substrateDropdownChoices',
  'researcherFacultyOptions',
  'facultyOptions',
  'researchGroupMap',
  'status',
  'projects',
  'sampleReportingCategory',
];

/** Shared width for Filter / Apply / Reset on the toolbar (matches bulk search panel). */
const sampleSearchToolbarActionButtonSx = { minWidth: 120 };

/** Responsive equal-width cells so filter rows align instead of random minWidths. */
const sampleSearchFilterGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: 1.5,
  alignItems: 'flex-start',
  width: '100%',
} as const;

const sampleSearchFieldFillSx = { width: '100%', minWidth: 0 } as const;

/** Initial `localFilters` for selection mode: presets merged with defaults, else persisted store criteria. */
function getInitialSelectionLocalFilters(
  persistedFilters: SampleSearchCriteria,
  selectionInitialFilters?: Partial<SampleSearchCriteria>
): SampleSearchCriteria {
  const hasInitial = selectionInitialFilters != null && Object.keys(selectionInitialFilters).length > 0;
  if (hasInitial) {
    return { ...defaultFilterCriteria, ...selectionInitialFilters } as SampleSearchCriteria;
  }
  return persistedFilters;
}

interface SampleSearchPanelProps {
  /** Custom title for the search panel */
  title?: string;
  /** When true, the large title row is omitted (e.g. parent dialog shows the title in its header). */
  hideTitleRow?: boolean;
  /** Whether this panel is used in a selection dialog (hides action buttons) */
  isSelectionMode?: boolean;
  /**
   * When `isSelectionMode`, merged with defaults for the initial panel buffer (lazy `useState` init).
   * The sample selection dialog should remount this panel when this object meaningfully changes (`key` on the parent).
   * Ignored when {@link filters} is provided (controlled selection).
   */
  selectionInitialFilters?: Partial<SampleSearchCriteria>;
  /** Optional controlled filters (e.g. selection dialog seeds criteria and avoids store→local sync loops) */
  filters?: SampleSearchCriteria;
  /** Callback when filters should be applied to the grid/store (selection dialog + controlled mode) */
  onFiltersChange?: (filters: SampleSearchCriteria, sourceFlags?: FilterSourceFlags) => void;
  /** Optional controlled pagination (for selection dialogs) */
  paginationModel?: { page: number; pageSize: number };
  /** Callback when pagination should reset on filter apply (selection dialog) */
  onPaginationModelChange?: (model: { page: number; pageSize: number }) => void;
  /**
   * `samples` = full sample search UI. `substrates` = inventory pool (no growth filters;
   * always searches with specimenType `substrates`).
   */
  mode?: SampleListMode;
}

export const SampleSearchPanel = ({
  title,
  hideTitleRow = false,
  isSelectionMode = false,
  selectionInitialFilters,
  filters: controlledFilters,
  onFiltersChange,
  paginationModel: controlledPaginationModel,
  onPaginationModelChange,
  mode = 'samples',
}: SampleSearchPanelProps = {}) => {
  const isSubstratesMode = mode === 'substrates';
  const pageDefaultCriteria = isSubstratesMode ? substrateDefaultFilterCriteria : defaultFilterCriteria;

  const {
    setFilters,
    setPaginationModel,
    filters: storeFilters,
    isSearching,
    setIsSearching,
    ensureFreshSampleGridServerRequest,
  } = useSampleListFilterStore(isSelectionMode ? 'samples' : mode);

  const isControlled = controlledFilters !== undefined;
  const { options, loading, error } = useSelectOptions(OPTION_KEYS);
  const { isAuthorized } = usePrivileges();
  const backend = useBackend();
  const systemConfig = useSystemConfig();
  const router = useRouter();

  const { showProjectSelectionDialog } = useDialogStore();
  const currentUser = useAuthStore((state) => state.currentUser);
  const hasGlobalSampleCreate = isAuthorized(PrivilegeEnum.Sample, 'create');
  const [hasProjectSampleCreate, setHasProjectSampleCreate] = useState(false);
  const canAddSample = hasGlobalSampleCreate || hasProjectSampleCreate || canCreateWithoutProject(isAuthorized);

  const isStoreBackedToolbar = !isSelectionMode && !isControlled;

  /**
   * Unsaved edits for the main samples grid: `null` = follow persisted `storeFilters` (rehydrate updates the store).
   * Selection / controlled dialogs keep a non-null buffer and push to parent or store on each change (no mirror effect).
   */
  const [filterDraft, setFilterDraft] = useState<SampleSearchCriteria | null>(() => {
    if (isStoreBackedToolbar) return null;
    if (controlledFilters !== undefined) return { ...controlledFilters };
    return getInitialSelectionLocalFilters(storeFilters, selectionInitialFilters);
  });

  const localFilters: SampleSearchCriteria = isStoreBackedToolbar
    ? (filterDraft ?? storeFilters)
    : (filterDraft as SampleSearchCriteria);

  const isSnapshotKind =
    !isSubstratesMode && (localFilters.kind ?? SampleSearchKind.Both) === SampleSearchKind.Snapshot;

  const selectedSampleSearchKind = localFilters.kind ?? SampleSearchKind.Both;
  const selectedSampleSearchKindTooltip = useMemo(
    () => SAMPLE_SEARCH_KIND_OPTIONS.find((opt) => opt.value === selectedSampleSearchKind)?.tooltip ?? '',
    [selectedSampleSearchKind]
  );

  const flushSelectionSearch = useCallback(
    (criteria: SampleSearchCriteria) => {
      if (!isSelectionMode) return;
      const cleanFilters = applySampleSearchKind({ ...criteria }, criteria.kind ?? SampleSearchKind.Both);
      delete cleanFilters.researchGroup;
      if (isControlled && onFiltersChange) {
        onFiltersChange(cleanFilters);
        onPaginationModelChange?.({ page: 0, pageSize: controlledPaginationModel?.pageSize ?? 25 });
      } else {
        setFilters(cleanFilters);
        setPaginationModel((prevModel) => ({
          ...prevModel,
          page: 0,
        }));
      }
    },
    [
      isSelectionMode,
      isControlled,
      onFiltersChange,
      onPaginationModelChange,
      controlledPaginationModel,
      setFilters,
      setPaginationModel,
    ]
  );

  const setLocalFilters = useCallback(
    (next: SampleSearchCriteria | ((prev: SampleSearchCriteria) => SampleSearchCriteria)) => {
      if (isStoreBackedToolbar) {
        setFilterDraft((prevDraft) => {
          const base = prevDraft ?? useSampleFilterStore.getState().filters;
          return typeof next === 'function' ? (next as (p: SampleSearchCriteria) => SampleSearchCriteria)(base) : next;
        });
        return;
      }
      const merged =
        typeof next === 'function' ? (next as (p: SampleSearchCriteria) => SampleSearchCriteria)(localFilters) : next;
      setFilterDraft(merged);
      flushSelectionSearch(merged);
    },
    [isStoreBackedToolbar, flushSelectionSearch, localFilters]
  );

  // Those are only calculcated, from basic options and some local filters.
  // No need for effects here.
  const researcherOptions = useMemo(() => {
    const facultyId = localFilters.addedByResearchGroup;
    if (facultyId && options.researchGroupMap?.has(facultyId)) {
      return options.researchGroupMap?.get(facultyId) ?? [];
    }
    // Role-based list (not faculty-linked) so researchers without FacultyID still appear (e.g. Q-NEXT).
    return options.researcherFacultyOptions ?? [];
  }, [localFilters.addedByResearchGroup, options.researchGroupMap, options.researcherFacultyOptions]);

  // Filtered researcher options for "Grown by" section
  const grownByResearcherOptions = useMemo(() => {
    const facultyId = localFilters.grownByResearchGroup;
    if (facultyId && options.researchGroupMap?.has(facultyId)) {
      return options.researchGroupMap?.get(facultyId) ?? [];
    }
    return options.researcherFacultyOptions ?? [];
  }, [localFilters.grownByResearchGroup, options.researchGroupMap, options.researcherFacultyOptions]);

  const instrumentOptions = useMemo(() => {
    const base = options.synthesisInstruments ?? [];
    const formatted = options.synthesisInstrumentOptions ?? [];

    // Filter by technique if specified
    const filteredIds = localFilters.techniqueId
      ? new Set(
          base.filter((inst) => inst.synthesisTechniques?.includes(localFilters.techniqueId!)).map((inst) => inst.id)
        )
      : null;

    // If filtering, return only matching formatted options; otherwise return all formatted options
    return filteredIds ? formatted.filter((opt) => filteredIds.has(opt.key)) : formatted;
  }, [options.synthesisInstruments, options.synthesisInstrumentOptions, localFilters.techniqueId]);

  const characterizationTechniqueOptions = useMemo(
    () =>
      (options.characterizationTechniques ?? [])
        .filter((t): t is CharacterizationTechniqueDto & { id: string; name: string } => !!t.id && !!t.name)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [options.characterizationTechniques]
  );

  const characterizationInstrumentOptionsFiltered = useMemo(() => {
    const base = options.characterizationInstruments ?? [];
    const formatted = options.characterizationInstrumentOptions ?? [];
    const techId = localFilters.characterizationTechniqueId;
    if (!techId) {
      return formatted;
    }
    const matchingIds = new Set(
      base
        .filter((inst) => inst.characterizationTechnique === techId)
        .map((inst) => inst.id)
        .filter(Boolean) as string[]
    );
    return formatted.filter((opt) => matchingIds.has(opt.key));
  }, [
    options.characterizationInstruments,
    options.characterizationInstrumentOptions,
    localFilters.characterizationTechniqueId,
  ]);

  /** Top-level techniques only (no parent). Used in the basic row. */
  const topLevelTechniques = useMemo(() => options.synthesisTechniques ?? [], [options.synthesisTechniques]);

  /** Leaf-only techniques for the extended "Detailed technique" dropdown. */
  const leafTechniques = useMemo(
    () => options.synthesisTechniquesLeafOnly ?? [],
    [options.synthesisTechniquesLeafOnly]
  );

  /** Resolve basic-row value: show top-level technique, or parent when a leaf technique is selected. */
  const basicTechniqueValue = useMemo(() => {
    const id = localFilters.techniqueId;
    if (!id) return null;
    const inTop = topLevelTechniques.find((t) => t.id === id);
    if (inTop) return inTop;
    const technique = leafTechniques.find((t) => t.id === id);
    const parentId = technique?.parentTechnique;
    if (!parentId) return null;
    return topLevelTechniques.find((t) => t.id === parentId) ?? null;
  }, [localFilters.techniqueId, topLevelTechniques, leafTechniques]);
  const [isExpanded, setIsExpanded] = useState(false);
  useReopenSearchPanelOnFailure(setIsExpanded);
  const [createSampleAnchorEl, setCreateSampleAnchorEl] = useState<null | HTMLElement>(null);
  const createSampleMenuOpen = Boolean(createSampleAnchorEl);
  /** Tracks quick growth-date preset so we can clear it when user edits dates manually */
  const [growthDatePreset, setGrowthDatePreset] = useState<'' | '30' | '90'>('');
  /** True when user changed "Added By" in the extended panel (so we don't treat it as "My samples" from basic row). */
  const [addedByTouchedInExtendedPanel, setAddedByTouchedInExtendedPanel] = useState(false);

  const [isMaterialDialogOpen, setIsMaterialDialogOpen] = useState(false);
  const { showAddToInventoryDialog, showCombineStartingMaterialsDialog, showMultiSampleGrowthDialog } =
    useDialogStore();

  const reportingCategoryOptions = useMemo(
    () => options.sampleReportingCategory ?? [],
    [options.sampleReportingCategory]
  );
  const selectedReportingCategory = useMemo(
    () =>
      reportingCategoryOptions.find((category) => category.code && category.code === localFilters.reportingCategory) ??
      null,
    [reportingCategoryOptions, localFilters.reportingCategory]
  );

  // Ensure unique project IDs; include a synthetic row for a persisted filter label not yet in the loaded list.
  const projectOptions = useMemo(() => {
    const projects = Array.from(
      new Map(
        options.projects
          ?.filter((p) => p.id !== undefined && p.projectLabel !== undefined)
          .map((p) => [p.id!, { id: p.id!, label: p.projectLabel! }])
      ).values()
    );
    const selectedLabel =
      localFilters.projectId && localFilters.projectId !== SAMPLE_SEARCH_NO_PROJECT
        ? localFilters.projectId.trim()
        : '';
    const hasSelectedInList = selectedLabel !== '' && projects.some((p) => p.label === selectedLabel);
    const fallback =
      selectedLabel !== '' && !hasSelectedInList
        ? [{ id: `__saved_filter__:${selectedLabel}`, label: selectedLabel }]
        : [];
    return isSnapshotKind ? [...fallback, ...projects] : [{ id: -1, label: 'No Project' }, ...fallback, ...projects];
  }, [options.projects, localFilters.projectId, isSnapshotKind]);

  const selectedProjectAutocompleteValue = useMemo(() => {
    if (localFilters.projectId === SAMPLE_SEARCH_NO_PROJECT) {
      return projectOptions.find((p) => p.id === -1) ?? null;
    }
    const label = localFilters.projectId?.trim();
    if (!label) return null;
    return projectOptions.find((p) => p.label === label) ?? null;
  }, [localFilters.projectId, projectOptions]);

  // Handle global dialog actions
  const handleAddToInventory = async () => {
    setCreateSampleAnchorEl(null);
    try {
      const result = await showAddToInventoryDialog();
      if (result) {
        log.trace('[SampleSearchPanel] Added to inventory:', result.samples);
        invalidateSampleGridCache(isSubstratesMode ? 'substrates' : undefined);
        ensureFreshSampleGridServerRequest();
        if (result.samples.length === 1) {
          const label = result.samples[0]?.sampleLabel;
          if (label) {
            const basePath = systemConfig?.basePath ?? '';
            router.push(`${basePath}/samples/${encodeURIComponent(label)}`);
          }
        }
      }
    } catch (error) {
      log.error('[SampleSearchPanel] Failed to add to inventory:', error);
    }
  };

  const handleAddSampleFromPrecursors = async () => {
    setCreateSampleAnchorEl(null);
    try {
      const result = await showCombineStartingMaterialsDialog();
      if (result) {
        log.trace('[SampleSearchPanel] Added sample from startingMaterials:', result);
        invalidateSampleGridCache();
        const label = result.sampleLabel;
        if (label) {
          const basePath = systemConfig?.basePath ?? '';
          router.push(`${basePath}/samples/${encodeURIComponent(label)}`);
        }
      }
    } catch (error) {
      log.error('[SampleSearchPanel] Failed to add sample from precursors:', error);
    }
  };

  const handleMultiSampleGrowth = async () => {
    setCreateSampleAnchorEl(null);
    try {
      const result = await showMultiSampleGrowthDialog();
      if (result) {
        log.trace('[SampleSearchPanel] Multi-sample growth created:', result);
        invalidateSampleGridCache();
        const label = result.sampleLabel;
        if (label) {
          const basePath = systemConfig?.basePath ?? '';
          router.push(`${basePath}/samples/${encodeURIComponent(label)}`);
        }
      }
    } catch (error) {
      log.error('[SampleSearchPanel] Failed to create multi-sample growth:', error);
    }
  };

  const materialOptions = options.sampleMaterialOptions ?? [];

  const sd = options.substrateDropdownChoices;

  const substrateNameOptions = useMemo(() => uniqueSortedDropdownStrings(sd?.names), [sd?.names]);

  const substrateVendorOptions = useMemo(() => uniqueSortedDropdownStrings(sd?.vendors), [sd?.vendors]);

  const substrateLotOptions = useMemo(() => uniqueSortedDropdownStrings(sd?.lots), [sd?.lots]);

  const substrateOrientationOptions = useMemo(() => uniqueSortedDropdownStrings(sd?.orientations), [sd?.orientations]);

  const sampleStatusOptions = useMemo(
    () => [
      {
        id: StatusDto.Pending,
        value: STATUS_MAP[StatusDto.Pending].label,
      },
      {
        id: StatusDto.Submitted,
        value: STATUS_MAP[StatusDto.Submitted].label,
      },
    ],
    []
  );

  const handleGrownByFacultySelected = (_: SyntheticEvent, selected: KeyValueType | null) => {
    const facultyId = selected?.key || '';
    setLocalFilters({ ...localFilters, grownByResearchGroup: facultyId, grownByUserId: '' });
  };

  const handleTechniqueChanged = (techniqueId: string) => {
    const filteredInstruments = techniqueId
      ? options.synthesisInstruments?.filter((inst) => inst.synthesisTechniques?.includes(techniqueId))
      : options.synthesisInstruments;

    const stillValid = filteredInstruments?.some((i) => i.id === localFilters.instrumentId);

    setLocalFilters({
      ...localFilters,
      techniqueId,
      instrumentId: stillValid ? localFilters.instrumentId : '',
    });
  };

  const handleCharacterizationTechniqueChanged = (characterizationTechniqueId: string) => {
    const filtered =
      characterizationTechniqueId && options.characterizationInstruments
        ? options.characterizationInstruments.filter(
            (inst) => inst.characterizationTechnique === characterizationTechniqueId
          )
        : options.characterizationInstruments;
    const stillValid = filtered?.some((i) => i.id === localFilters.characterizationInstrumentId);
    setLocalFilters({
      ...localFilters,
      characterizationTechniqueId,
      characterizationInstrumentId: stillValid ? localFilters.characterizationInstrumentId : '',
    });
  };

  // Show add-sample buttons if user has Sample create globally or on at least one project they collaborate on.
  useEffect(() => {
    if (hasGlobalSampleCreate) return;
    let cancelled = false;
    backend.project
      .hasAnyProjectWithPrivilege('Sample', OperationEnum.Create)
      .then((has) => {
        if (!cancelled) setHasProjectSampleCreate(has);
      })
      .catch(() => {
        if (!cancelled) setHasProjectSampleCreate(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasGlobalSampleCreate, backend.project]);

  const handleApply = useCallback(() => {
    markNextSearchUserInitiated();
    ensureFreshSampleGridServerRequest();
    invalidateSampleGridCache(isSubstratesMode ? 'substrates' : 'samples');
    setIsSearching(true);
    // Remove the deprecated researchGroup field before applying filters
    let cleanFilters = { ...localFilters };
    delete cleanFilters.researchGroup;
    if (isSubstratesMode) {
      cleanFilters.specimenType = 'substrates';
      cleanFilters.kind = SampleSearchKind.Active;
    } else {
      cleanFilters = applySampleSearchKind(cleanFilters, cleanFilters.kind ?? SampleSearchKind.Both);
    }
    log.debug('[SampleSearchPanel] Applying sample filters:', cleanFilters);
    // Note: Date objects are serialized by the store's persist middleware when saved to localStorage.
    const growthFromBasic = growthDatePreset === '30' || growthDatePreset === '90';
    const addedByFromBasic =
      !!cleanFilters.addedByUserId &&
      !!currentUser?.id &&
      cleanFilters.addedByUserId === currentUser.id &&
      !addedByTouchedInExtendedPanel;
    const sourceFlags: FilterSourceFlags = {
      growthDateSetFromBasicRow: growthFromBasic,
      addedBySetFromBasicRow: addedByFromBasic,
    };
    if (isControlled && onFiltersChange) {
      onFiltersChange(cleanFilters, sourceFlags);
      if (onPaginationModelChange) {
        const pageSize = controlledPaginationModel?.pageSize ?? 25;
        onPaginationModelChange({ page: 0, pageSize });
      }
    } else {
      setFilters(cleanFilters, sourceFlags);
      setFilterDraft(null);
      setPaginationModel((prevModel) => ({
        ...prevModel,
        page: 0,
      }));
    }
    setAddedByTouchedInExtendedPanel(false);
    // Close the filter panel
    setIsExpanded(false);
  }, [
    localFilters,
    ensureFreshSampleGridServerRequest,
    isControlled,
    onFiltersChange,
    onPaginationModelChange,
    controlledPaginationModel,
    setFilters,
    setPaginationModel,
    growthDatePreset,
    currentUser,
    addedByTouchedInExtendedPanel,
    setIsExpanded,
    setIsSearching,
    isSubstratesMode,
  ]);

  const handleProjectLookup = async () => {
    const projects = await showProjectSelectionDialog('Select Project to Filter');
    if (projects && projects.length > 0) {
      // Take first project for single selection filter
      const project = projects[0];
      setLocalFilters({
        ...localFilters,
        projectId: project.projectLabel || '',
      });
    }
    // If user cancels, don't change the filter (could be "No Project" or empty)
  };

  const handleReset = useCallback(() => {
    invalidateSampleGridCache(isSubstratesMode ? 'substrates' : 'samples');
    setGrowthDatePreset('');
    // Remove any deprecated researchGroup field from the default criteria
    let cleanDefaultCriteria = { ...pageDefaultCriteria };
    delete cleanDefaultCriteria.researchGroup;
    if (!isSubstratesMode) {
      cleanDefaultCriteria = buildDefaultSampleSearchCriteriaForUser(currentUser, cleanDefaultCriteria);
    }
    if (isControlled && onFiltersChange) {
      onFiltersChange(cleanDefaultCriteria, { growthDateSetFromBasicRow: false, addedBySetFromBasicRow: false });
      if (onPaginationModelChange) {
        onPaginationModelChange({ page: 0, pageSize: controlledPaginationModel?.pageSize ?? 25 });
      }
    } else {
      setFilters(cleanDefaultCriteria, { growthDateSetFromBasicRow: false, addedBySetFromBasicRow: false });
    }
    if (isStoreBackedToolbar) {
      setFilterDraft(null);
    } else {
      setFilterDraft(cleanDefaultCriteria);
      flushSelectionSearch(cleanDefaultCriteria);
    }
  }, [
    isControlled,
    onFiltersChange,
    onPaginationModelChange,
    controlledPaginationModel,
    setFilters,
    isStoreBackedToolbar,
    flushSelectionSearch,
    isSubstratesMode,
    pageDefaultCriteria,
    currentUser,
  ]);

  const handleSearchOnEnter = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      if (isSearching) return;
      handleApply();
    },
    [handleApply, isSearching]
  );

  if (loading) {
    return (
      <Box sx={{ width: '98%', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body1">Loading...</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ width: '98%', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body1">Loading...</Typography>
      </Box>
    );
  }

  const hasUserTechniques = (currentUser?.techniques?.length ?? 0) > 0;

  return (
    <Box sx={{ width: '100%' }}>
      {!hideTitleRow ? (
        <Box display="flex" alignItems="center" gap={2} sx={{ mt: isSelectionMode ? 1 : 2, mb: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {title || (isSubstratesMode ? 'Search Substrates' : 'Search Samples')}
          </Typography>
          {!isSelectionMode && canAddSample ? (
            <Box display="flex" gap={1} sx={{ ml: 'auto' }}>
              {isSubstratesMode ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddToInventory}
                  size="large"
                  sx={{
                    minWidth: '180px',
                    fontWeight: 500,
                  }}
                  startIcon={<Inventory />}
                >
                  Add substrate
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={(e) => setCreateSampleAnchorEl(e.currentTarget)}
                    size="large"
                    sx={{
                      minWidth: '180px',
                      fontWeight: 500,
                    }}
                    endIcon={<ArrowDropDown />}
                  >
                    Add Sample
                  </Button>
                  <Menu
                    anchorEl={createSampleAnchorEl}
                    open={createSampleMenuOpen}
                    onClose={() => setCreateSampleAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem onClick={handleAddToInventory}>
                      <ListItemIcon>
                        <Inventory fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Add to Inventory</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleAddSampleFromPrecursors}>
                      <ListItemIcon>
                        <Inventory fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Combine Starting Materials</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={handleMultiSampleGrowth}>
                      <ListItemIcon>
                        <Inventory fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Multi-Sample Growth</ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          ) : null}
        </Box>
      ) : null}

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1.5}
        onKeyDown={handleSearchOnEnter}
        sx={{
          mb: 1.5,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'background.paper',
          py: 1,
          borderRadius: 1,
        }}
      >
        <Box sx={{ ...sampleSearchFilterGridSx, flex: 1, minWidth: 0 }}>
          <TextField
            label="Sample ID or Alias"
            variant="outlined"
            size="small"
            fullWidth
            value={localFilters.sampleLabel}
            onChange={(e) => setLocalFilters({ ...localFilters, sampleLabel: e.target.value })}
            sx={sampleSearchFieldFillSx}
          />
          <EntityAutocompleteWithLookup
            label="Project"
            size="small"
            fullWidth
            sx={sampleSearchFieldFillSx}
            options={projectOptions}
            value={selectedProjectAutocompleteValue}
            onChange={(value) => {
              const selectedValue = value as { id: number; label: string } | null;
              if (selectedValue?.id === -1) {
                if (isSnapshotKind) return;
                setLocalFilters({ ...localFilters, projectId: SAMPLE_SEARCH_NO_PROJECT });
              } else {
                setLocalFilters({ ...localFilters, projectId: selectedValue?.label || '' });
              }
            }}
            onLookupClick={handleProjectLookup}
            lookupTooltip="Search and select project"
          />
          {!isSubstratesMode ? (
            <Autocomplete<SynthesisTechniqueDto>
              size="small"
              fullWidth
              sx={sampleSearchFieldFillSx}
              options={topLevelTechniques}
              getOptionLabel={(option) => option?.name ?? ''}
              value={basicTechniqueValue}
              onChange={(_, newValue) => handleTechniqueChanged(newValue?.id ?? '')}
              renderInput={(params) => <TextField {...params} label="Technique" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          ) : null}
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            fullWidth
            sx={sampleSearchFieldFillSx}
            options={materialOptions ?? []}
            getOptionLabel={(option): string => option.name || ''}
            filterOptions={(opts, params) => filterAutocompleteOptionsByMatchRank(opts, params, (o) => o.name || '')}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={
              (localFilters.elements?.length ?? 0) > 0
                ? [{ id: -1, name: `${localFilters.elements!.join(', ')} + *` }]
                : (materialOptions?.filter(
                    (m): m is typeof m & { id: number } =>
                      m.id !== undefined && (localFilters.materials ?? []).includes(m.id)
                  ) ?? [])
            }
            onChange={(_, selectedOptions) =>
              setLocalFilters({
                ...localFilters,
                materials: selectedOptions.map((opt) => opt.id).filter((id): id is number => id !== undefined),
                elements: [],
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Material"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {params.InputProps.endAdornment}
                      <IconButton size="small" onClick={() => setIsMaterialDialogOpen(true)} sx={{ ml: 0.5 }}>
                        <Search fontSize="small" />
                      </IconButton>
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
          />
          {!isSubstratesMode ? (
            <FormControl size="small" fullWidth sx={sampleSearchFieldFillSx}>
              <InputLabel>Growth date</InputLabel>
              <Select
                value={growthDatePreset}
                label="Growth date"
                onChange={(e) => {
                  const preset = e.target.value as '' | '30' | '90';
                  setGrowthDatePreset(preset);
                  const now = new Date();
                  if (preset === '30') {
                    setLocalFilters({
                      ...localFilters,
                      grownAfter: startOfDay(subDays(now, 30)),
                      grownBefore: endOfDay(now),
                    });
                  } else if (preset === '90') {
                    setLocalFilters({
                      ...localFilters,
                      grownAfter: startOfDay(subDays(now, 90)),
                      grownBefore: endOfDay(now),
                    });
                  } else {
                    setLocalFilters({
                      ...localFilters,
                      grownAfter: undefined,
                      grownBefore: undefined,
                    });
                  }
                }}
              >
                <MenuItem value="">
                  <em>Any</em>
                </MenuItem>
                <MenuItem value="30">Last 30 days</MenuItem>
                <MenuItem value="90">Last 90 days</MenuItem>
              </Select>
            </FormControl>
          ) : null}
          {hasUserTechniques ? (
            <FormControlLabel
              sx={{ alignSelf: 'center', justifySelf: 'start', m: 0 }}
              control={
                <Checkbox
                  checked={!!(currentUser?.id && localFilters.addedByUserId === currentUser.id)}
                  onChange={(_, checked) => {
                    setAddedByTouchedInExtendedPanel(false);
                    const addedByUserId = checked && currentUser?.id ? currentUser.id : '';
                    const kind = checked
                      ? SampleSearchKind.Active
                      : defaultSampleSearchKindForUser(currentUser, { addedByUserId });
                    setLocalFilters(
                      applySampleSearchKind(
                        {
                          ...localFilters,
                          addedByUserId,
                        },
                        kind
                      )
                    );
                  }}
                  disabled={!currentUser?.id || !hasUserTechniques}
                />
              }
              label="My samples"
            />
          ) : null}
        </Box>

        <Box
          display="flex"
          flexWrap="wrap"
          gap={1.5}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ flexShrink: 0 }}
        >
          <Badge
            color="primary"
            variant="dot"
            invisible={
              !(isSubstratesMode
                ? hasSubstrateExtendedFiltersSet(localFilters, {
                    addedBySetFromBasicRow:
                      !!localFilters.addedByUserId &&
                      !!currentUser?.id &&
                      localFilters.addedByUserId === currentUser.id &&
                      !addedByTouchedInExtendedPanel,
                  })
                : hasExtendedFiltersSet(localFilters, {
                    growthDateSetFromBasicRow: growthDatePreset === '30' || growthDatePreset === '90',
                    addedBySetFromBasicRow:
                      !!localFilters.addedByUserId &&
                      !!currentUser?.id &&
                      localFilters.addedByUserId === currentUser.id &&
                      !addedByTouchedInExtendedPanel,
                    topLevelTechniqueIds: topLevelTechniques.map((t) => t.id).filter((id): id is string => id != null),
                  }))
            }
            sx={{ '& .MuiBadge-badge': { right: 8, top: 8 } }}
          >
            <Button
              variant={isExpanded ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setIsExpanded(!isExpanded)}
              startIcon={<FilterList />}
              sx={sampleSearchToolbarActionButtonSx}
            >
              Filter
            </Button>
          </Badge>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={sampleSearchToolbarActionButtonSx}
            size="small"
            disabled={isSearching}
            startIcon={isSearching ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isSearching ? 'Searching…' : 'Search'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            sx={sampleSearchToolbarActionButtonSx}
            size="small"
            color="error"
          >
            Reset
          </Button>
        </Box>
      </Box>

      {isExpanded ? (
        <Box sx={{ width: '100%', mb: isSelectionMode ? 1 : 1.5, pt: 0.5, px: 1.5, pb: 1 }}>
          {/* 1. Status & scope */}
          <Box display="flex" flexWrap="wrap" gap={1.5} mt={0.5} alignItems="center">
            <Typography
              variant="caption"
              sx={{
                width: '100%',
                px: 1,
                py: 0.5,
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
              }}
            >
              Status & scope
            </Typography>
            <Box sx={sampleSearchFilterGridSx}>
              {!isSubstratesMode ? (
                <FormControl size="small" fullWidth sx={sampleSearchFieldFillSx}>
                  <InputLabel id="sample-search-kind-label">Which samples</InputLabel>
                  <Tooltip title={selectedSampleSearchKindTooltip} placement="top" arrow>
                    <Box sx={{ width: '100%' }}>
                      <Select
                        labelId="sample-search-kind-label"
                        id="sample-search-kind"
                        value={selectedSampleSearchKind}
                        label="Which samples"
                        onChange={(e) => {
                          const kind = e.target.value as SampleSearchKind;
                          setLocalFilters(applySampleSearchKind(localFilters, kind));
                        }}
                      >
                        {SAMPLE_SEARCH_KIND_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value} title={opt.tooltip}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  </Tooltip>
                </FormControl>
              ) : null}
              {!isSnapshotKind ? (
                <FormControl size="small" fullWidth sx={sampleSearchFieldFillSx}>
                  <InputLabel>Sample Status</InputLabel>
                  <Select
                    value={localFilters.status ?? ''}
                    onChange={(e) => setLocalFilters({ ...localFilters, status: e.target.value })}
                    label="Sample Status"
                  >
                    <MenuItem value="">
                      <em>Any</em>
                    </MenuItem>
                    {sampleStatusOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              {!isSnapshotKind ? (
                <Autocomplete<ReportingCategoryDto>
                  fullWidth
                  sx={sampleSearchFieldFillSx}
                  options={reportingCategoryOptions}
                  getOptionLabel={(option) => option.label ?? option.code ?? ''}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id ?? option.code ?? ''}>
                      {option.label ?? option.code ?? ''}
                      {option.code && option.label && option.label !== option.code ? ` (${option.code})` : ''}
                    </li>
                  )}
                  value={selectedReportingCategory}
                  onChange={(_, selected) =>
                    setLocalFilters({ ...localFilters, reportingCategory: selected?.code ?? '' })
                  }
                  isOptionEqualToValue={(option, value) =>
                    (option.id !== undefined && value.id !== undefined && option.id === value.id) ||
                    (option.code !== undefined && value.code !== undefined && option.code === value.code)
                  }
                  renderInput={(params) => <TextField {...params} label="Reporting Category" size="small" />}
                />
              ) : null}
            </Box>
            <Box display="flex" flexWrap="wrap" gap={2} sx={{ px: 1, mt: 0.5 }} alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!localFilters.includeSplitPieces}
                    onChange={(e) => setLocalFilters({ ...localFilters, includeSplitPieces: e.target.checked })}
                  />
                }
                label="Include pieces of split samples"
              />
              {!isSubstratesMode ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!localFilters.multiSampleGrowthOnly}
                      onChange={(_, checked) =>
                        setLocalFilters({ ...localFilters, multiSampleGrowthOnly: checked || undefined })
                      }
                    />
                  }
                  label="Only Multi-Sample growth"
                  title="When checked, show only child samples from a shared growth run (e.g. RUN.1, RUN.2). Growth-run parent rows never appear in search; the run label is in the Growth Run column."
                />
              ) : null}
              {!isSubstratesMode ? (
                <FormControl size="small" sx={{ ...sampleSearchFieldFillSx, minWidth: 280 }}>
                  <InputLabel id="specimen-type-filter-label">Specimen type</InputLabel>
                  <Select
                    labelId="specimen-type-filter-label"
                    value={localFilters.specimenType ?? ''}
                    onChange={(e) => setLocalFilters({ ...localFilters, specimenType: e.target.value })}
                    label="Specimen type"
                  >
                    <MenuItem value="">
                      <em>All samples</em>
                    </MenuItem>
                    <MenuItem value="substrates">Substrates only (no growth yet)</MenuItem>
                    <MenuItem value="grown">Grown samples only</MenuItem>
                  </Select>
                </FormControl>
              ) : null}
            </Box>
          </Box>

          {/* 2. Added to inventory */}
          <Box display="flex" flexWrap="wrap" gap={1.5} mt={1.5} alignItems="center">
            <Typography
              variant="caption"
              sx={{
                width: '100%',
                px: 1,
                py: 0.5,
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
              }}
            >
              Added to inventory
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5} sx={{ width: '100%' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box sx={sampleSearchFilterGridSx}>
                  <Autocomplete
                    fullWidth
                    sx={sampleSearchFieldFillSx}
                    options={researcherOptions ?? []}
                    getOptionLabel={(option) => option.value}
                    renderOption={(props, option) => (
                      <li {...props} key={option.key}>
                        {option.value}
                      </li>
                    )}
                    value={researcherOptions?.find((g) => g.key === localFilters.addedByUserId) || null}
                    onChange={(_, selected) => {
                      setAddedByTouchedInExtendedPanel(true);
                      setLocalFilters({ ...localFilters, addedByUserId: selected?.key || '' });
                    }}
                    renderInput={(params) => <TextField {...params} label="Added By" size="small" />}
                  />
                  <Autocomplete
                    fullWidth
                    sx={sampleSearchFieldFillSx}
                    options={options.facultyOptions ?? []}
                    getOptionLabel={(option) => option.value}
                    renderOption={(props, option) => (
                      <li {...props} key={option.key}>
                        {option.value}
                      </li>
                    )}
                    value={options.facultyOptions?.find((g) => g.key === localFilters.addedByResearchGroup) || null}
                    onChange={(_, newValue) =>
                      setLocalFilters({ ...localFilters, addedByResearchGroup: newValue?.key || '' })
                    }
                    renderInput={(params) => <TextField {...params} label="Group" size="small" />}
                  />
                  <DatePicker
                    label="Date added from"
                    value={localFilters.addedAfter ?? null}
                    onChange={(date) => setLocalFilters({ ...localFilters, addedAfter: date ?? undefined })}
                    slotProps={{
                      textField: { fullWidth: true, size: 'small', sx: sampleSearchFieldFillSx },
                    }}
                  />
                  <DatePicker
                    label="Date added to"
                    value={localFilters.addedBefore ?? null}
                    onChange={(date) => setLocalFilters({ ...localFilters, addedBefore: date ?? undefined })}
                    slotProps={{
                      textField: { fullWidth: true, size: 'small', sx: sampleSearchFieldFillSx },
                    }}
                  />
                </Box>
              </LocalizationProvider>
              <Box sx={sampleSearchFilterGridSx}>
                <Autocomplete
                  fullWidth
                  sx={sampleSearchFieldFillSx}
                  options={substrateNameOptions}
                  value={localFilters.substrateName || null}
                  onChange={(_, value) => setLocalFilters({ ...localFilters, substrateName: value || '' })}
                  renderInput={(params) => <TextField {...params} label="Substrate Name" size="small" />}
                />
                <Autocomplete
                  fullWidth
                  sx={sampleSearchFieldFillSx}
                  options={substrateVendorOptions}
                  value={localFilters.substrateVendor || null}
                  onChange={(_, value) => setLocalFilters({ ...localFilters, substrateVendor: value || '' })}
                  renderInput={(params) => <TextField {...params} label="Substrate Vendor" size="small" />}
                />
                <Autocomplete
                  fullWidth
                  sx={sampleSearchFieldFillSx}
                  options={substrateLotOptions}
                  value={localFilters.substrateLot || null}
                  onChange={(_, value) => setLocalFilters({ ...localFilters, substrateLot: value || '' })}
                  renderInput={(params) => <TextField {...params} label="Substrate Lot" size="small" />}
                />
                <Autocomplete
                  fullWidth
                  sx={sampleSearchFieldFillSx}
                  options={substrateOrientationOptions}
                  value={localFilters.substrateOrientation || null}
                  onChange={(_, value) => setLocalFilters({ ...localFilters, substrateOrientation: value || '' })}
                  renderInput={(params) => <TextField {...params} label="Substrate Orientation" size="small" />}
                />
                <TextField
                  type="number"
                  label="Substrate purity (%)"
                  size="small"
                  fullWidth
                  sx={sampleSearchFieldFillSx}
                  value={localFilters.substratePurity ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setLocalFilters({ ...localFilters, substratePurity: undefined });
                      return;
                    }
                    const n = Number(raw);
                    setLocalFilters({
                      ...localFilters,
                      substratePurity: Number.isFinite(n) ? n : undefined,
                    });
                  }}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                />
              </Box>
            </Box>
          </Box>

          {!isSubstratesMode ? (
            <Box display="flex" flexWrap="wrap" gap={1.5} mt={1.5} alignItems="center">
              <Typography
                variant="caption"
                sx={{
                  width: '100%',
                  px: 1,
                  py: 0.5,
                  fontWeight: 600,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                }}
              >
                Growth
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box sx={sampleSearchFilterGridSx}>
                  <Autocomplete
                    fullWidth
                    sx={sampleSearchFieldFillSx}
                    options={grownByResearcherOptions ?? []}
                    getOptionLabel={(option) => option.value}
                    renderOption={(props, option) => (
                      <li {...props} key={option.key}>
                        {option.value}
                      </li>
                    )}
                    value={grownByResearcherOptions?.find((g) => g.key === localFilters.grownByUserId) || null}
                    onChange={(_, selected) => setLocalFilters({ ...localFilters, grownByUserId: selected?.key || '' })}
                    renderInput={(params) => <TextField {...params} label="Grown By" size="small" />}
                  />
                  <Autocomplete
                    fullWidth
                    sx={sampleSearchFieldFillSx}
                    options={options.facultyOptions ?? []}
                    getOptionLabel={(option) => option.value}
                    renderOption={(props, option) => (
                      <li {...props} key={option.key}>
                        {option.value}
                      </li>
                    )}
                    value={options.facultyOptions?.find((g) => g.key === localFilters.grownByResearchGroup) || null}
                    onChange={handleGrownByFacultySelected}
                    renderInput={(params) => <TextField {...params} label="Research Group" size="small" />}
                  />
                  <DatePicker
                    label="Growth from"
                    value={localFilters.grownAfter ?? null}
                    onChange={(date) => {
                      setGrowthDatePreset('');
                      setLocalFilters({ ...localFilters, grownAfter: date ?? undefined });
                    }}
                    slotProps={{
                      textField: { fullWidth: true, size: 'small', sx: sampleSearchFieldFillSx },
                    }}
                  />
                  <DatePicker
                    label="Growth to"
                    value={localFilters.grownBefore ?? null}
                    onChange={(date) => {
                      setGrowthDatePreset('');
                      setLocalFilters({ ...localFilters, grownBefore: date ?? undefined });
                    }}
                    slotProps={{
                      textField: { fullWidth: true, size: 'small', sx: sampleSearchFieldFillSx },
                    }}
                  />
                  <Autocomplete<SynthesisTechniqueDto>
                    fullWidth
                    sx={sampleSearchFieldFillSx}
                    options={leafTechniques}
                    getOptionLabel={(option) => option?.name ?? ''}
                    value={leafTechniques.find((t) => t.id === localFilters.techniqueId) ?? null}
                    onChange={(_, newValue) => handleTechniqueChanged(newValue?.id ?? '')}
                    renderInput={(params) => <TextField {...params} label="Detailed technique" size="small" />}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />
                  <Autocomplete
                    fullWidth
                    sx={sampleSearchFieldFillSx}
                    options={instrumentOptions}
                    getOptionLabel={(option) => option.value}
                    value={instrumentOptions.find((opt) => opt.key === localFilters.instrumentId) ?? null}
                    onChange={(_, newValue) => setLocalFilters({ ...localFilters, instrumentId: newValue?.key ?? '' })}
                    renderInput={(params) => <TextField {...params} label="Instrument" size="small" />}
                  />
                </Box>
              </LocalizationProvider>
            </Box>
          ) : null}

          {/* Characterization (allowed before growth) */}
          <Box display="flex" flexWrap="wrap" gap={1.5} mt={1.5} alignItems="center">
            <Typography
              variant="caption"
              sx={{
                width: '100%',
                px: 1,
                py: 0.5,
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
              }}
            >
              Characterization
            </Typography>
            <Box sx={sampleSearchFilterGridSx}>
              <Autocomplete<CharacterizationTechniqueDto>
                fullWidth
                sx={sampleSearchFieldFillSx}
                options={characterizationTechniqueOptions}
                getOptionLabel={(option) => option?.name ?? ''}
                value={
                  characterizationTechniqueOptions.find((t) => t.id === localFilters.characterizationTechniqueId) ??
                  null
                }
                onChange={(_, newValue) => handleCharacterizationTechniqueChanged(newValue?.id ?? '')}
                renderInput={(params) => <TextField {...params} label="Characterization technique" size="small" />}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
              <Autocomplete
                fullWidth
                sx={sampleSearchFieldFillSx}
                options={characterizationInstrumentOptionsFiltered}
                getOptionLabel={(option) => option.value}
                value={
                  characterizationInstrumentOptionsFiltered.find(
                    (opt) => opt.key === localFilters.characterizationInstrumentId
                  ) ?? null
                }
                onChange={(_, newValue) =>
                  setLocalFilters({ ...localFilters, characterizationInstrumentId: newValue?.key ?? '' })
                }
                renderInput={(params) => <TextField {...params} label="Characterization instrument" size="small" />}
              />
            </Box>
          </Box>

          {/* 5. Shipping */}
          <Box
            display="flex"
            flexWrap="wrap"
            gap={1.5}
            mt={1.5}
            alignItems="center"
            sx={isSnapshotKind ? { opacity: 0.55 } : undefined}
          >
            <Typography
              variant="caption"
              sx={{
                width: '100%',
                px: 1,
                py: 0.5,
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
              }}
            >
              Shipping
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={sampleSearchFilterGridSx}>
                <FormControl size="small" fullWidth sx={sampleSearchFieldFillSx} disabled={isSnapshotKind}>
                  <InputLabel>Shipping Status</InputLabel>
                  <Select
                    value={localFilters.shipped ?? ''}
                    label="Shipping Status"
                    onChange={(e) =>
                      setLocalFilters({
                        ...localFilters,
                        shipped: e.target.value,
                        shippedAfter: undefined,
                        shippedBefore: undefined,
                      })
                    }
                  >
                    <MenuItem value="">
                      <em>Any</em>
                    </MenuItem>
                    <MenuItem value="no">Not Shipped</MenuItem>
                    <MenuItem value="completed">Shipped</MenuItem>
                    <MenuItem value="notCompleted">Shipping Initiated, not yet completed</MenuItem>
                    <MenuItem value="startedOrCompleted">Shipping Initiated</MenuItem>
                  </Select>
                </FormControl>
                {localFilters.shipped === 'completed' || localFilters.shipped === 'notCompleted' ? (
                  <>
                    <DatePicker
                      label="Shipping from"
                      value={localFilters.shippedAfter ?? null}
                      disabled={isSnapshotKind}
                      onChange={(date) => setLocalFilters({ ...localFilters, shippedAfter: date ?? undefined })}
                      slotProps={{
                        textField: { fullWidth: true, size: 'small', sx: sampleSearchFieldFillSx },
                      }}
                    />
                    <DatePicker
                      label="Shipping to"
                      value={localFilters.shippedBefore ?? null}
                      disabled={isSnapshotKind}
                      onChange={(date) => setLocalFilters({ ...localFilters, shippedBefore: date ?? undefined })}
                      slotProps={{
                        textField: { fullWidth: true, size: 'small', sx: sampleSearchFieldFillSx },
                      }}
                    />
                  </>
                ) : null}
              </Box>
            </LocalizationProvider>
          </Box>
        </Box>
      ) : null}
      <MaterialFilterDialog
        open={isMaterialDialogOpen}
        onClose={() => setIsMaterialDialogOpen(false)}
        onApply={(selectedIds, selectedElements) => {
          setLocalFilters({
            ...localFilters,
            materials: selectedIds,
            elements: selectedElements,
          });
        }}
        selectedMaterialIds={localFilters.materials ?? []}
        selectedElements={localFilters.elements ?? []}
        allMaterials={(materialOptions ?? []).filter(
          (m): m is { id: number; name: string } => m.id !== undefined && m.name !== undefined
        )}
      />
    </Box>
  );
};
